import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, Certificate, EnvelopeSimple, Globe, MapPin, ShieldCheck } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';

const modeLabel = (m) => m.replace(/_/g, ' ');

const ProviderProfile = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/directory/${slug}`)
      .then((r) => setProfile(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: profile
      ? `${profile.company} — ${(profile.categories || [])[0] || 'Service Provider'} in ${profile.country_name} | Vox Meridian`
      : 'Provider Profile | Vox Meridian',
    description: profile
      ? (profile.tagline || `${profile.company} — founder-verified ${(profile.categories || []).join(', ')} provider in ${profile.country_name}. Contact directly via Vox Meridian.`)
      : '',
    path: `/directory/${slug}`,
    jsonLd: profile
      ? {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: profile.company,
          description: profile.tagline || profile.about?.slice(0, 200) || '',
          url: `https://voxmeridian.com/directory/${slug}`,
          email: profile.email,
          ...(profile.website ? { sameAs: [profile.website.startsWith('http') ? profile.website : `https://${profile.website}`] } : {}),
          areaServed: profile.markets.map((m) => ({ '@type': 'Place', name: m })),
        }
      : null,
  });

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-start justify-center bg-ink px-6 lg:px-10" data-testid="profile-not-found">
        <p className="mono-label text-volt">404</p>
        <h1 className="mt-4 font-display text-5xl font-black tracking-tight text-parchment">Profile not found.</h1>
        <p className="mt-4 text-steel">This provider profile doesn't exist or hasn't been approved yet.</p>
        <Link to="/directory" data-testid="profile-not-found-back" className="mt-8 bg-volt px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5">
          Browse the directory
        </Link>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-ink" data-testid="profile-loading">
        <p className="mono-label text-steel">Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="bg-ink" data-testid="provider-profile-page">
      <PageHero eyebrow={profile.type === 'showcase' ? 'Showcase provider' : 'Verified provider'} lines={[profile.company]}>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          {profile.has_photo && (
            <img
              src={`${API}/directory/photo/${profile.slug}`}
              alt={`${profile.company} logo`}
              className="h-16 w-16 border border-line object-cover"
              data-testid="profile-photo"
            />
          )}
          <span className="flex items-center gap-1.5 border border-volt/40 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-volt" data-testid="profile-verified-badge">
            <ShieldCheck size={14} weight="bold" /> Founder-verified
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-steel" data-testid="profile-country">
            <MapPin size={13} weight="bold" className="text-volt" /> {profile.country_name}
          </span>
        </div>
        {profile.tagline && (
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-parchment" data-testid="profile-tagline">{profile.tagline}</p>
        )}
      </PageHero>

      <section className="border-b border-line bg-ink">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-8">
            {profile.about && (
              <div data-testid="profile-about">
                <Chapter no="01" label="About" title="Who they are." />
                <Reveal delay={0.05}>
                  <p className="mt-8 whitespace-pre-line text-base leading-relaxed text-steel">{profile.about}</p>
                </Reveal>
              </div>
            )}

            <div className="mt-14" data-testid="profile-services">
              <Chapter no={profile.about ? '02' : '01'} label="Services" title="What they do." />
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {profile.categories.map((c) => (
                  <Reveal key={c} delay={0.05}>
                    <div className="border border-line bg-surface p-5">
                      <p className="text-sm font-semibold text-parchment">{c}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              {profile.delivery_modes?.length > 0 && (
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-steel" data-testid="profile-delivery-modes">
                  Delivery: {profile.delivery_modes.map(modeLabel).join(' · ')}
                </p>
              )}
            </div>

            {profile.certifications?.length > 0 && (
              <div className="mt-14" data-testid="profile-certifications">
                <Chapter no="03" label="Credentials" title="Certifications." />
                <ul className="mt-8 space-y-3">
                  {profile.certifications.map((c) => (
                    <li key={c} className="flex items-start gap-2.5 text-base text-parchment">
                      <Certificate size={17} weight="bold" className="mt-0.5 shrink-0 text-volt" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <Reveal className="lg:sticky lg:top-24">
              <div className="border border-line bg-surface p-8" data-testid="profile-contact-card">
                <p className="mono-label text-volt">Contact directly</p>
                <p className="mt-3 text-sm leading-relaxed text-steel">
                  No commission, no middleman — you deal with the provider from the first message.
                </p>
                <a
                  href={`mailto:${profile.email}`}
                  data-testid="profile-email-link"
                  className="mt-6 flex items-center gap-2 break-all border border-line bg-ink px-4 py-3 text-sm text-parchment transition-colors duration-200 hover:border-volt/60 hover:text-volt"
                >
                  <EnvelopeSimple size={16} className="shrink-0 text-volt" /> {profile.email}
                </a>
                {profile.website && (
                  <a
                    href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="profile-website-link"
                    className="mt-3 flex items-center gap-2 border border-line bg-ink px-4 py-3 text-sm text-parchment transition-colors duration-200 hover:border-volt/60 hover:text-volt"
                  >
                    <Globe size={16} className="shrink-0 text-volt" /> {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                <div className="mt-6 border-t border-line pt-5">
                  <p className="mono-label text-steel">Markets served</p>
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="profile-markets">
                    {profile.markets.map((m) => (
                      <span key={m} className="border border-line px-2.5 py-1 text-[11px] text-steel">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-volt text-ink" data-testid="profile-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-16 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <div>
            <p className="mono-label text-ink/70">Need work like this done?</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Post one RFQ — get matched with verified providers.
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/buyers"
              data-testid="profile-rfq-cta"
              className="group flex shrink-0 items-center justify-center gap-2 bg-ink px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-transform duration-200 hover:-translate-y-0.5"
            >
              Post an RFQ — free
              <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/directory"
              data-testid="profile-directory-link"
              className="flex shrink-0 items-center justify-center gap-2 border border-ink px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-ink hover:text-volt"
            >
              Browse directory
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProviderProfile;
